/**
 * /dashboard/accounting/reports/msme-overdue — MSME-overdue (G_ACC8).
 *
 * MSMED Act 2006 §15-16: bills owed to MSME-classified suppliers past
 * the 45-day window, with the §16 interest ESTIMATED the way the statute
 * computes it — compound interest with monthly rests at three times the
 * RBI bank rate (QA books #9; it used to be 9% p.a. simple). The bank
 * rate is an input (the server's default until changed); the CSV export
 * uses the same rate.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/reports/msme-overdue/page.tsx.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Download, Info } from "lucide-react";
import { accountingApi } from "@/lib/api";
import { bpsToPercent, parseBankRatePercent } from "@/lib/msme";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { EmptyState } from "@/components/shared/empty-state";
import { useSmPlus } from "@/lib/use-sm-plus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPaise } from "@/lib/expense-format";
import { formatDate } from "@/lib/format";
import { msmeOverdueCSVURL } from "@/lib/api/accounting";
import { reportKeys } from "@/hooks/use-accounting";
import { toApiError } from "@/lib/api";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/reports/msme-overdue",
)({
  component: MSMEOverdueRoute,
});

function todayUTCDate(): string {
  const d = new Date();
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function MSMEOverdueRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [asOf, setAsOf] = useState<string>(todayUTCDate());
  const [pending, setPending] = useState<string>(asOf);
  // RBI bank rate for the §16 estimate: null = the server's default.
  const [bankRateBps, setBankRateBps] = useState<number | null>(null);
  // null = untouched (the field shows the rate the report used).
  const [pendingRate, setPendingRate] = useState<string | null>(null);
  const query = useQuery({
    queryKey: [...reportKeys.msmeOverdue(asOf), bankRateBps ?? "default-rate"],
    queryFn: ({ signal }) =>
      accountingApi.getMSMEOverdue(asOf, signal, bankRateBps ?? undefined),
  });
  const data = query.data ?? null;
  const isSmPlus = useSmPlus();
  const loading = query.isPending;
  const error = query.isError ? toApiError(query.error).message : null;
  const usedBank = data?.bank_rate_bps ?? bankRateBps ?? null;
  const usedAnnual = data?.interest_rate_bps ?? null;
  const rateInput = pendingRate ?? (usedBank != null ? bpsToPercent(usedBank) : "");
  const parsedRate = parseBankRatePercent(rateInput);
  const rateDirty = parsedRate != null && parsedRate !== usedBank;

  return (
    <AccountingModuleShell
      title="MSME Overdue"
      description="Bills owed to MSME-classified suppliers past the 45-day clock."
      actions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/accounting/reports" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
          <Button asChild className="gap-1.5">
            <a href={msmeOverdueCSVURL(asOf, bankRateBps ?? undefined)} download>
              <Download className="h-4 w-4" />
              Export CSV
            </a>
          </Button>
        </div>
      }
    >
      <Card>
        <CardContent className="grid gap-3 py-4 sm:grid-cols-[1fr_1fr_auto]">
          <div className="grid max-w-xs gap-1.5">
            <Label htmlFor="msme-asof">As Of</Label>
            <Input
              id="msme-asof"
              type="date"
              value={pending}
              onChange={(e) => setPending(e.target.value)}
            />
          </div>
          <div className="grid max-w-xs gap-1.5">
            <Label htmlFor="msme-bank-rate">RBI Bank Rate (%)</Label>
            <Input
              id="msme-bank-rate"
              inputMode="decimal"
              value={rateInput}
              onChange={(e) => setPendingRate(e.target.value)}
              aria-invalid={rateInput !== "" && parsedRate == null}
              aria-describedby="msme-bank-rate-help"
              data-testid="msme-bank-rate"
            />
            <p id="msme-bank-rate-help" className="text-xs text-muted-foreground">
              {rateInput !== "" && parsedRate == null
                ? "Enter a rate above 0 and up to 20, e.g. 5.5."
                : "The rate RBI has notified — check rbi.org.in if it has changed."}
            </p>
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              onClick={() => {
                setAsOf(pending);
                if (parsedRate != null) setBankRateBps(parsedRate);
              }}
              disabled={!pending || (pending === asOf && !rateDirty) || (rateInput !== "" && parsedRate == null)}
            >
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Alert className="mt-4">
        <Info className="h-4 w-4" />
        <AlertTitle>About the interest column</AlertTitle>
        <AlertDescription>
          An estimate of the interest MSMED Act §16 charges on late payment:
          compound interest with monthly rests at three times the RBI bank
          rate
          {usedBank != null && usedAnnual != null
            ? ` (${bpsToPercent(usedBank)}% × 3 = ${bpsToPercent(usedAnnual)}% a year)`
            : ""}
          , from the day after each bill&apos;s 45-day due date. The
          statutory clock runs from acceptance of the goods or services,
          which can differ from the bill date, so confirm the figures with
          your CA before paying or disclosing them.
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Could not load MSME-overdue report</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="mt-4 space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : data ? (
        <div className="mt-4 space-y-4">
          <Card>
            <CardContent className="grid gap-2 py-4 text-sm sm:grid-cols-3">
              <div>
                <span className="text-muted-foreground">Overdue bills</span>
                <p className="text-base font-medium">{data.count}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total outstanding</span>
                <p className="text-base font-medium tabular-nums">
                  {formatPaise(data.total_outstanding_minor)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">
                  Total interest (estimate)
                </span>
                <p className="text-base font-medium tabular-nums">
                  {formatPaise(data.total_interest_minor)}
                </p>
              </div>
            </CardContent>
          </Card>

          {data.rows.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title="No MSME Bills Overdue"
              description={`Nothing is past the 45-day MSME due date as of ${formatDate(data.as_of)}.`}
            />
          ) : !isSmPlus ? (
            // Phones: one row per bill — vendor and outstanding on top,
            // bill no. · Udyam · days overdue · interest underneath.
            <ul className="divide-y rounded-md border" data-testid="msme-overdue-cards">
              {data.rows.map((r) => (
                <li key={r.bill_id} className="space-y-1 px-3 py-2.5 text-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 break-words font-medium text-gray-900">
                      {r.vendor_name}
                    </span>
                    <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums">
                      {formatPaise(r.outstanding_minor)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="font-mono">{r.bill_no}</span>
                    {r.vendor_udyam_registration_no ? (
                      <span className="font-mono">{r.vendor_udyam_registration_no}</span>
                    ) : r.vendor_msme_udyam ? (
                      <span>No valid Udyam no.</span>
                    ) : null}
                    <span className="whitespace-nowrap">
                      {r.days_overdue} {r.days_overdue === 1 ? "day" : "days"} overdue
                    </span>
                    <span className="whitespace-nowrap tabular-nums">
                      Interest {formatPaise(r.interest_minor)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bill #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Udyam</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Days Overdue</TableHead>
                  <TableHead className="text-right">Interest (Estimate)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                    <TableRow key={r.bill_id}>
                      <TableCell className="font-mono text-xs">
                        {r.bill_no}
                      </TableCell>
                      <TableCell>{r.vendor_name}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.vendor_udyam_registration_no ||
                          (r.vendor_msme_udyam ? (
                            <span title="Marked MSME by an old note. MSME-1 needs the vendor's Udyam number — add it on the contact.">
                              {r.vendor_msme_udyam} (no valid Udyam no.)
                            </span>
                          ) : (
                            "—"
                          ))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPaise(r.outstanding_minor)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.days_overdue}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPaise(r.interest_minor)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
          )}
        </div>
      ) : null}
    </AccountingModuleShell>
  );
}
